import { useCallback, useRef, useState } from "react";

// Debounced autosave. Call save(entryKey, payload, persistFn) on each edit;
// it debounces per-key and reports status: "saving" | "saved" | "error".
// flush() runs any still-pending saves immediately and resolves when they settle
// (used before submit so no recent edit is lost).
export function useAutosave(delay = 800) {
  const timers = useRef({});
  const pending = useRef({}); // key -> () => Promise  (the queued save invocation)
  const [statusByKey, setStatusByKey] = useState({});
  const [errorByKey, setErrorByKey] = useState({});

  const setStatus = useCallback((key, status) => {
    setStatusByKey((prev) => ({ ...prev, [key]: status }));
  }, []);

  const runSave = useCallback((key) => {
    const fn = pending.current[key];
    if (!fn) return Promise.resolve();
    delete pending.current[key];
    return fn();
  }, []);

  const save = useCallback(
    (key, payload, persistFn) => {
      setStatus(key, "saving");
      if (timers.current[key]) clearTimeout(timers.current[key]);
      // Queue the actual work so flush() can run it on demand too.
      pending.current[key] = async () => {
        try {
          const result = await persistFn(payload);
          setStatus(key, "saved");
          // A previous save on this key may have failed with an actionable
          // refusal -- once a later save on the same key succeeds, that
          // banner is stale and must go, or it accuses the user of an
          // error they already fixed.
          setErrorByKey((prev) => (prev[key] ? { ...prev, [key]: null } : prev));
          return result;
        } catch (e) {
          setStatus(key, "error");
          // A refusal the user can act on -- a closed year, a completed task --
          // is worth showing. A network blip is not; those keep the silent
          // retry behaviour.
          const body = e?.response?.data;
          const field = body && (body.year_end || body.task || body.detail);
          const msg = Array.isArray(field) ? field[0] : field;
          setErrorByKey((prev) => ({ ...prev, [key]: msg || null }));
          try {
            localStorage.setItem(`te_unsaved_${key}`, JSON.stringify(payload));
          } catch (_) {}
        }
      };
      timers.current[key] = setTimeout(() => { runSave(key); }, delay);
    },
    [delay, setStatus, runSave]
  );

  // Run all still-pending (debounced-but-not-yet-fired) saves now and await them.
  const flush = useCallback(async () => {
    const keys = Object.keys(pending.current);
    keys.forEach((k) => {
      if (timers.current[k]) clearTimeout(timers.current[k]);
    });
    await Promise.all(keys.map((k) => runSave(k)));
  }, [runSave]);

  return { save, flush, statusByKey, errorByKey };
}
